import React, { useCallback, useRef } from 'react';
import { Grid } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFields } from 'utils/types';
import { SmsMessageModel } from '../../models';
import { useTranslate } from 'hooks/use-translate';
import { useFieldProps } from 'hooks';
import { calcHtmlTemplate } from 'utils';
import { validateRule } from 'AurionCR/components/formV2';
import { AppInput } from 'components/app-input';
import { useSourceMessageTemplates, MessageTemplate } from 'hooks/use-source-message-templates';
import { AppInputTemplate } from 'components/app-input-template';

interface Props<TP> {
  isLoading: boolean;
  templatesPayload: TP;
}

export const SendSmsMessageForm = <TP,>({ isLoading, templatesPayload }: Props<TP>) => {
  const { t } = useTranslate();

  const {
    formState: { errors },
    control,
    setValue,
  } = useFormContext<FormFields<SmsMessageModel>>();

  const sourceTemplates = useSourceMessageTemplates();

  const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });

  const messageRef = useRef<HTMLInputElement>(null);
  const onSelectTemplate = useCallback(
    (value: MessageTemplate | null) => {
      const message = calcHtmlTemplate(value?.message || '', templatesPayload ?? undefined);
      setValue('message', message);
      messageRef.current?.focus();
    },
    [setValue, templatesPayload],
  );

  const isDisabledTemplate = sourceTemplates.data.length === 0 || isLoading;
  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Controller
          control={control}
          name={'toPhoneNumber'}
          rules={validateRule('required')}
          render={({ field: renderProps }) => (
            <AppInput
              {...getFieldProps(renderProps)}
              label={t('mobile-phone')}
              disabled={isLoading}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <Controller
          control={control}
          name={'message'}
          rules={validateRule('required,minLength')}
          render={({ field: renderProps }) => {
            return (
              <AppInputTemplate
                {...getFieldProps(renderProps)}
                onSelectTemplate={onSelectTemplate}
                disabled={isLoading}
                inputRef={messageRef}
                templates={sourceTemplates.data}
                isLoading={sourceTemplates.loading}
                disabledTemplate={isDisabledTemplate}
              />
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
